import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { BulkFeatureKey } from '../../../bulk-select/enums/bulk-feature-key';
import { tryToGetItRollup, tryToPlaceHoldOnRollup } from '../../../entity/actions/entity.actions';
import { Entity, EntityTypes, FormatGroup, GetItRequest, HoldRequest } from '../../../entity/models/entity';
import { ListPagination } from '../../../list/models/list.dto';
import { loadMoreLibraryListItems } from '../../actions/library-list.actions';
import { I18NService } from 'app/services/i18-n.service';
import { BrowserTabTitleService } from 'core/services/browser-tab-title.service';
import {
  getItemsLoadingState,
  getItemsPagination,
  getLibraryList,
  getLibraryListItems,
  LibraryList,
  LoadingState,
  isLastPageLoaded,
} from '../../reducers/library-list.reducer';

@Component({
  selector: 'app-library-list-view',
  templateUrl: './library-list-view.component.html',
  styleUrls: ['./library-list-view.component.scss']
})
export class LibraryListViewComponent implements OnInit, OnDestroy {
  public libraryList: LibraryList;
  public libraryListType: string;
  public libraryCreatedFrom: string;
  public items: (Entity | FormatGroup)[];
  public filteredItems: (Entity | FormatGroup)[];
  public itemsCount: number;
  public itemsPagination: ListPagination;
  public itemsLoadingState: LoadingState;
  public allListItemsLoaded = false;
  private readonly subscriptions = new Subscription();
  public readonly entityTypes = EntityTypes;
  public readonly bulkFeatureKey = BulkFeatureKey.LIBRARY_LIST;
  public fixedHeader: boolean;
  public isListView = true;

  constructor(
    private readonly store: Store,
    private readonly i18nService: I18NService,
    private readonly cdr: ChangeDetectorRef,
    private readonly configLoader: ConfigurationLoader,
    private readonly titleService: BrowserTabTitleService
  ) {
  }

  public ngOnInit(): void {
    this.fixedHeader = this.configLoader.headerConfig?.fixedHeader;

    this.subscriptions.add(
      this.store.select(getLibraryList).subscribe((libraryList) => {
        this.libraryList = libraryList;
        this.libraryListType = libraryList.type;
        this.libraryCreatedFrom = libraryList.createdFrom ? libraryList.createdFrom.type : null;
        this.titleService.setTabTitle(libraryList.name);
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.store.select(getLibraryListItems).subscribe((libraryListItems) => {
        this.items = libraryListItems;
        this.filteredItems = libraryListItems;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.store.select(getItemsLoadingState).subscribe((itemsLoadingState) => {
        this.itemsLoadingState = itemsLoadingState;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.store.select(getItemsPagination).subscribe((itemsPagination) => {
        this.itemsPagination = itemsPagination;
        this.itemsCount = itemsPagination.totalResults;
        this.allListItemsLoaded = isLastPageLoaded(itemsPagination);
        this.cdr.markForCheck();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public getItForRollupCard(data: { item: GetItRequest }): void {
    this.store.dispatch(tryToGetItRollup({item: data.item}));
  }

  public placeHoldOnRollupCard(data: { item: HoldRequest }): void {
    this.store.dispatch(tryToPlaceHoldOnRollup({item: data.item}));
  }

  public loadMoreItems(): void {
    this.store.dispatch(loadMoreLibraryListItems());
  }

  public trackById(index: number, item: Entity): string {
    return item.id;
  }

  public onSearchTermChanged(searchTerm: string): void {
      if (searchTerm === '') {
          this.filteredItems = this.items;
      } else {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        this.filteredItems = this.items.filter((item: FormatGroup | Entity) => {
        const fullLanguage = this.i18nService.getLangFacetTitle((item as FormatGroup).language)?.toLowerCase();
          return (
            (item as FormatGroup).title?.toLowerCase().includes(lowerCaseSearchTerm) ||
            (item as Entity).label?.toLowerCase().includes(lowerCaseSearchTerm) ||
            (item as FormatGroup).primaryAgent?.label?.toLowerCase().includes(lowerCaseSearchTerm) ||
            (item as FormatGroup).publicationDate?.toLowerCase().includes(lowerCaseSearchTerm) ||
            (item as FormatGroup).seriesTitle?.toLowerCase().includes(lowerCaseSearchTerm) ||
            fullLanguage?.includes(lowerCaseSearchTerm) ||
            (item as FormatGroup).materialTabs?.some(
              (tab) => tab.locations?.some((location) => location.label?.toLowerCase().includes(lowerCaseSearchTerm))
            ) ||
            (item as FormatGroup).materialTabs?.some((tab) => tab.name?.toLowerCase().includes(lowerCaseSearchTerm))
          );
        });

      }
    }
}
